import type { FieldDef } from "./fields";

/** Normalise a header for comparison: lowercase, trim, collapse separators. */
function normalise(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[\s_-]+/g, "_");
}

/**
 * Best CSV header for a field: exact normalised key match first, then aliases.
 * Returns the raw header string, or null if nothing matches. Ported 1:1 from
 * the original tool's `findBestMatch`.
 */
export function findBestMatch(field: FieldDef, headers: string[]): string | null {
  const normalised = headers.map((raw) => ({ raw, norm: normalise(raw) }));

  const exact = normalised.find((h) => h.norm === field.key);
  if (exact) return exact.raw;

  for (const alias of field.aliases) {
    const aliasNorm = normalise(alias);
    const match = normalised.find((h) => h.norm === aliasNorm);
    if (match) return match.raw;
  }

  return null;
}

/** Build the initial field→header mapping by auto-matching every field. */
export function autoMatchAll(
  fields: readonly FieldDef[],
  headers: string[]
): { mapping: Record<string, string>; matchedCount: number } {
  const mapping: Record<string, string> = {};
  let matchedCount = 0;
  for (const field of fields) {
    const best = findBestMatch(field, headers);
    mapping[field.key] = best ?? "";
    if (best) matchedCount++;
  }
  return { mapping, matchedCount };
}
